import "./pagination.scss";

import DoubleRightArrowIcon from "@frontend/images/icons/DoubleRightArrowIcon";
import DoubleLeftArrowIcon from "@frontend/images/icons/DoubleLeftArrowIcon";
import RightArrowIcon from "@frontend/images/icons/RightArrowIcon";
import RightLeftIcon from "@frontend/images/icons/LeftArrowIcon";
import { Link, router } from "@inertiajs/react";
import useT from "@frontend/js/hooks/useT";
import { buildLocalePath } from "@frontend/js/utils/locale";

export default function Pagination({
    pagination,
    collection,
    className = "",
    onPageChange = null,
    isLoading = false,
    queryParams = {},
    pageSlug,
    locale = null,
}) {
    const { t } = useT("site");
    const paginationData = collection?.meta
        ? {
              current_page: collection.meta.current_page,
              last_page: collection.meta.last_page,
              per_page: collection.meta.per_page,
              total: collection.meta.total,
              has_more_pages:
                  collection.meta.current_page < collection.meta.last_page,
          }
        : pagination;

    const currentPage = Number(paginationData?.current_page || 1);
    const lastPage = Number(paginationData?.last_page || 1);

    if (!paginationData || lastPage <= 1) {
        return null;
    }

    const firstPageUrl = collection?.links?.first || null;
    const prevPageUrl = collection?.links?.prev || null;
    const nextPageUrl = collection?.links?.next || null;
    const lastPageUrl = collection?.links?.last || null;
    const isCallbackMode =
        typeof onPageChange === "function" ||
        Object.keys(queryParams).length > 0;

    const controls = [
        {
            id: "first",
            label: "First page",
            icon: <DoubleLeftArrowIcon />,
            page: 1,
            url: firstPageUrl,
            disabled: isCallbackMode
                ? currentPage <= 1
                : !firstPageUrl || currentPage <= 1,
        },
        {
            id: "prev",
            label: "Previous page",
            icon: <RightLeftIcon />,
            page: currentPage - 1,
            url: prevPageUrl,
            disabled: isCallbackMode
                ? currentPage <= 1
                : !prevPageUrl || currentPage <= 1,
        },
        {
            id: "next",
            label: "Next page",
            icon: <RightArrowIcon />,
            page: currentPage + 1,
            url: nextPageUrl,
            disabled: isCallbackMode
                ? currentPage >= lastPage
                : !nextPageUrl || currentPage >= lastPage,
        },
        {
            id: "last",
            label: "Last page",
            icon: <DoubleRightArrowIcon />,
            page: lastPage,
            url: lastPageUrl,
            disabled: isCallbackMode
                ? currentPage >= lastPage
                : !lastPageUrl || currentPage >= lastPage,
        },
    ];

    const renderControl = (control) => {
        if (control.disabled) {
            return (
                <span
                    key={control.id}
                    className="sharedPagination__inner__controls__link sharedPagination__inner__controls__link--disabled"
                    aria-disabled="true"
                    aria-label={control.label}
                >
                    <span
                        className="sharedPagination__inner__controls__link__icon"
                        aria-hidden="true"
                    >
                        {control.icon}
                    </span>
                </span>
            );
        }

        if (isCallbackMode) {
            return (
                <button
                    key={control.id}
                    type="button"
                    className="sharedPagination__inner__controls__link"
                    onClick={() => {
                        if (typeof onPageChange === "function") {
                            onPageChange(control.page);
                            return;
                        }

                        const params = { ...queryParams, page: control.page };
                        router.get(buildLocalePath(locale, pageSlug), params, {
                            preserveScroll: false,
                            onSuccess: () =>
                                window.scrollTo({ top: 0, behavior: "smooth" }),
                        });
                    }}
                    disabled={isLoading}
                    aria-label={control.label}
                >
                    <span
                        className="sharedPagination__inner__controls__link__icon"
                        aria-hidden="true"
                    >
                        {control.icon}
                    </span>
                </button>
            );
        }

        return (
            <Link
                key={control.id}
                className="sharedPagination__inner__controls__link"
                href={control.url}
                preserveState
                aria-label={control.label}
            >
                <span
                    className="sharedPagination__inner__controls__link__icon"
                    aria-hidden="true"
                >
                    {control.icon}
                </span>
            </Link>
        );
    };

    return (
        <div
            className={`sharedPagination ${className}`.trim()}
            aria-label="Pagination"
        >
            <div className="sharedPagination__inner">
                <div className="sharedPagination__inner__controls sharedPagination__inner__controls--left">
                    {controls.slice(0, 2).map(renderControl)}
                </div>

                <div
                    className="sharedPagination__inner__center"
                    aria-live="polite"
                >
                    <span className="sharedPagination__inner__center__centerText">
                        {t("pagination-page")} {currentPage}{" "}
                        {t("pagination-of")} {lastPage}
                    </span>
                </div>

                <div className="sharedPagination__inner__controls sharedPagination__inner__controls--right">
                    {controls.slice(2).map(renderControl)}
                </div>
            </div>
        </div>
    );
}
